/**
 * engine/cursor.ts —— 路径寻址纯函数（零框架）
 *
 * 与后端 `app/core/cursor.ts` 1:1 语义一致；前后端都按同一套 path 语义解析 /
 * 推进 / 逃逸，这是三端（service walker / engine StepPlayer / CLI Player）不
 * 分叉的前提。如果后端 cursor.ts 改了语义，这里必须同步——契约测试兜底。
 *
 * spec: docs/superpowers/specs/2026-04-26-stable-step-id-design.md
 *
 * Path 语义（spec §5）：所有段都是字符串，三种角色：
 *   1. Stable step ID（如 `"0005_dlg"` / `"0002p0001p0001_char"` 补丁步）
 *   2. Container escape key：`"options"` / `"steps"` / `"then"` / `"else"` /
 *      `"messages"`
 *   3. Choice option ID（如 `"opt_marry"`，由 ChoiceOption.id 提供）
 *
 * Concurrent group 不开新容器层 —— 组内 step 直接是父容器的 step ID（compiler
 * 跨组单调递增 seq），cursor 只在槽位层移动，emit 端把组打包成 Step[]。
 *
 * 本文件零类型依赖（root 取 unknown），运行时按 .id / Array.isArray 鸭子识别。
 *
 * 表面差异（与后端 cursor.ts 故意不同）：
 *   - 这里走纯 flat exports（`resolvePath` / `advanceSibling` / `popToParentSibling`）
 *     —— `StepPlayer` 是 OO 风格 + 零运行时类型依赖（root 是 unknown），扁平
 *     exports + destructure import 更自然。
 *   - 后端 `app/core/cursor.ts` 把同样的函数包在 `Cursor` namespace 里
 *     —— 那边 walker / cursor-valid / save-action-service 都按命名空间消费，
 *     `Cursor.at(...)` 这类 helper 也属于该命名空间。
 *   - 两端语义完全等价；契约测试以"path 操作行为"为基准（不锁 export 形状）。
 */
const ESCAPE_KEYS = new Set(["options", "steps", "then", "else", "messages"]);
/** 在 StepOrConcurrentGroup[]（duck-typed）容器里按 id 查 step + 槽位信息
 *
 * Defensive：若容器单 step 槽位的 `.id` 缺失/空（违反 spec §4：容器里的 step 必带
 * ID；唯一例外 nested @else @if 的 IfStep 出现在 IfStep.else 字段里，不进容器），
 * 则抛错而不是静默 fall-through —— silent miss 会让 advanceSibling 返 null 导致
 * walker 早退（这是历史 cursor bug 的根因之一）。
 */
function findStepSlot(container, id) {
    if (!Array.isArray(container))
        return null;
    for (let i = 0; i < container.length; i++) {
        const slot = container[i];
        if (Array.isArray(slot)) {
            for (const inner of slot) {
                if (inner && typeof inner === "object" && inner.id === id) {
                    return { slotIndex: i, group: slot, step: inner };
                }
            }
        }
        else if (slot && typeof slot === "object") {
            const slotId = slot.id;
            if (typeof slotId !== "string" || slotId.length === 0) {
                const slotType = slot.type;
                throw new Error(`[cursor.findStepSlot] container slot at index ${i} (type=${String(slotType)}) ` +
                    `is missing required \`id\` (looking up "${id}"); this indicates a compiler ` +
                    `bug or stale vendored data. spec §4 requires every container step to bear a ` +
                    `stable step id.`);
            }
            if (slotId === id) {
                return { slotIndex: i, group: null, step: slot };
            }
        }
    }
    return null;
}
/** 给定 path，返回末段 step 的"父容器"（步槽位序列）+ 父 path */
function getParentContainer(episode, path) {
    if (path.length === 0)
        return null;
    const parentPath = path.slice(0, -1);
    if (parentPath.length === 0) {
        return { container: episode.steps, parentPath: [] };
    }
    const parentNode = resolveNode(episode, parentPath);
    if (!Array.isArray(parentNode))
        return null;
    return { container: parentNode, parentPath };
}
function resolveNode(episode, path) {
    let node = episode.steps;
    for (const seg of path) {
        if (node === null || node === undefined)
            return undefined;
        if (Array.isArray(node)) {
            // 优先尝试 StepOrConcurrentGroup[]（descend into groups）
            const slot = findStepSlot(node, seg);
            if (slot) {
                node = slot.step;
                continue;
            }
            // 否则当作 ChoiceOption[] / TextMessageStep[]，顶层 .id 比较
            const direct = node.find((x) => x && typeof x === "object" && x.id === seg);
            node = direct;
            continue;
        }
        if (typeof node === "object") {
            if (!ESCAPE_KEYS.has(seg))
                return undefined;
            node = node[seg];
            continue;
        }
        return undefined;
    }
    return node;
}
/** 按 path 在 episode.steps 根结构中逐段下钻；未命中返 undefined */
export function resolvePath(root, path) {
    if (root === null || root === undefined)
        return undefined;
    // root 既可能是整个 EpisodeShape（含 .steps），也可能是 steps 数组本身
    // —— StepPlayer 一直传 episode.steps 进来。统一包装成 { steps } 走 resolveNode。
    const stepsArray = Array.isArray(root)
        ? root
        : root.steps;
    if (!Array.isArray(stepsArray))
        return undefined;
    return resolveNode({ steps: stepsArray }, path);
}
/**
 * 在当前父容器中往后走一个槽位。
 *
 * 与 v1 区别：v1 是纯 path 操作（最后段 +1）；v2 必须看 episode 结构 —— step
 * ID 不可计算，只能查父容器找下一个槽位。
 *
 * 返回 null：当前已是父容器最后一个槽位；caller 应 fall-through 到
 * popToParentSibling。
 *
 * 假设：path 末段是 step ID（cursor 当前停在某 step 上）。
 */
export function advanceSibling(root, path) {
    if (path.length === 0)
        return null;
    const stepsArray = Array.isArray(root)
        ? root
        : root.steps;
    if (!Array.isArray(stepsArray))
        return null;
    const lastId = path[path.length - 1];
    if (lastId === undefined)
        return null; // unreachable (path.length > 0) — strict-index guard
    const parent = getParentContainer({ steps: stepsArray }, path);
    if (!parent)
        return null;
    const slot = findStepSlot(parent.container, lastId);
    if (!slot)
        return null; // step 不在父容器（spec §3 fail-fast 锚点）
    const nextSlotIndex = slot.slotIndex + 1;
    if (nextSlotIndex >= parent.container.length)
        return null;
    const nextSlot = parent.container[nextSlotIndex];
    let nextId;
    if (Array.isArray(nextSlot)) {
        const first = nextSlot[0];
        nextId = first && typeof first === "object"
            ? first.id
            : undefined;
    }
    else if (nextSlot && typeof nextSlot === "object") {
        nextId = nextSlot.id;
    }
    if (!nextId)
        return null;
    return [...parent.parentPath, nextId];
}
/**
 * 离开当前容器，回到父级 halt step 的下一个兄弟槽位。
 *
 * 路径结构（spec §5）—— 合法 escape 形态全集（不止 4 种；含嵌套 @else @if）：
 *   [..., "X", "0001_dlg"]                                              flat top-level
 *   [..., "X", "then", stepID]                                          if.then
 *   [..., "X", "else", stepID]                                          if.else (Step[])
 *   [..., "X", "else", "then", stepID]                                  if.else: IfStep, nested .then
 *   [..., "X", "else", "else", stepID]                                  if.else: IfStep, nested .else (Step[])
 *   [..., "X", "else", "else", ..., "then", stepID]                     深嵌套 @else @if 链
 *   [..., "X", "steps", stepID]                                         cg_show / minigame body
 *   [..., "X", "options", optID, "steps", stepID]                       choice option body
 *   [..., "X", "messages", messageID]                                   phone_show.messages
 *
 * 算法 —— "由右向左剥离 + 不变量驱动"：
 *   1. cursor = path.slice(0, -1)（弹掉无效末段）
 *   2. 循环到 cursor 空：
 *      a. 若末段前一段是 "options"（即末段是 option ID）→ 再弹一段，continue
 *      b. 若末段是 escape key（then / else / messages / steps / options）→ 再弹
 *         一段，continue
 *      c. 否则末段是 step ID → 调 advanceSibling；命中返；未命中则把这个 step
 *         也弹掉，continue（递归剥离父层）
 *   3. 全部弹空 → null（episode 尾）
 *
 * 不变量：cursor 在循环 step c 入口时，末段总是父容器中合法 step ID。能否
 * advanceSibling 的判断完全交给 advanceSibling，不依赖 ID 字符串形态
 * （4 位数字 / 补丁步 4_p_4_p_4 都不需要 regex 区分）。
 */
export function popToParentSibling(root, path) {
    if (path.length === 0)
        return null;
    const stepsArray = Array.isArray(root)
        ? root
        : root.steps;
    if (!Array.isArray(stepsArray))
        return null;
    let cursor = path.slice(0, -1);
    while (cursor.length > 0) {
        const last = cursor[cursor.length - 1];
        if (last === undefined)
            break; // unreachable (cursor.length > 0) — strict-index guard
        const prev = cursor.length >= 2 ? cursor[cursor.length - 2] : undefined;
        // (a) 末段是 option ID（前一段为 "options"）—— 跳过
        if (prev === "options") {
            cursor = cursor.slice(0, -1);
            continue;
        }
        // (b) 末段是 container escape key —— 跳过
        if (ESCAPE_KEYS.has(last)) {
            cursor = cursor.slice(0, -1);
            continue;
        }
        // (c) 末段是 step ID —— 在父容器中找下一兄弟
        const next = advanceSibling(stepsArray, cursor);
        if (next !== null)
            return next;
        // 父容器已到末位 —— 把这个 step 也剥掉，继续向上剥
        cursor = cursor.slice(0, -1);
    }
    return null;
}
/** path 合法性软校验：非空、所有段非空字符串 */
export function isValidPath(path) {
    if (!Array.isArray(path) || path.length === 0)
        return false;
    for (const seg of path) {
        if (typeof seg !== "string" || seg.length === 0)
            return false;
    }
    return true;
}
export function clonePath(path) {
    return [...path];
}
export function serializePath(path) {
    return path.join(".");
}
